import { useRef, useMemo, useEffect } from "react";
import { useTexture } from "@react-three/drei";
import { ObjectSpaceNormalMap, RepeatWrapping } from "three";
import { RoundedBox } from "@react-three/drei";
import { rotateUVs } from "@/services/helpers/rotateUV";
import { PlankProps } from "@/services/interfaces";
import useCopyTexture from "@/hooks/useCopyTexture";
import { useAppStore } from "@/stores/appStore";
import * as THREE from "three";

export default function Plank({
	material,
	position,
	width,
	height,
	length,
	rotation,
	radius = 0.003,
	bevelSegments = 2,
	smoothness = 2,
	textures,
	pillarPosition,
}: PlankProps) {
	const { gate } = useAppStore((state) => state.configurator);
	const meshRef = useRef();
	const lastGeometryRef = useRef<THREE.BufferGeometry | null>(null);

	useEffect(() => {
		const mesh = meshRef.current;
		if (!mesh) return;

		const geometry = mesh.geometry;
		if (!geometry) return;

		// Check if the geometry is new
		if (lastGeometryRef.current !== geometry) {
			rotateUVs(geometry, {
				front: 180,
				left: 270,
				right: 270,
				back: 180,
				top: 0,
				bottom: 0,
			});

			// Store the new geometry reference
			lastGeometryRef.current = geometry;
		}
	}, [width, height, length]); // Runs only when geometry might change

	return (
		<group>
			<RoundedBox
				args={[width, height, length]}
				radius={radius} // Bevel radius
				smoothness={smoothness} // Higher for smoother bevel
				bevelSegments={bevelSegments}
				receiveShadow
				castShadow
				ref={meshRef}
				rotation={rotation}
				material={material}
				position={[position.x, position.y, position.z]}>
				<meshStandardMaterial
					{...textures}
					color={gate.selectedMaterials[gate.id]?.settings.color}
					roughness={gate.selectedMaterials[gate.id]?.settings.roughness}
					normalScale={new THREE.Vector2(pillarPosition === "right" ? 1 : -1, 1)}
				/>
			</RoundedBox>
		</group>
	);
}
